import React, { useState } from "react";
import { Fade, Slide } from "react-awesome-reveal";
import { BsArrowUpRight } from "react-icons/bs";
import Slider from "../carousel/Slider";
import { projectData } from "../projectsData";

// --- Updated Sub-Component for Dynamic Desktop vs Mobile Paragraph Limits ---
const ProjectDescription = ({ fullDescription }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Split description by double line breaks
  const paragraphs = fullDescription.split("\n\n");

  if (paragraphs.length <= 1) {
    return (
      <p style={{ whiteSpace: "pre-line" }} className="short-note text-[1rem] leading-[1.5] font-normal lg:mb-[1.5rem] xl:mb-[2rem] max-w-[26.25rem]">
        {fullDescription}
      </p>
    );
  }

  return (
    <div className="max-w-[26.25rem] lg:mb-[1.5rem] xl:mb-[2rem]">

      {/* --- PARAGRAPH 1 --- 
          Always visible on both mobile and desktop */}
      <p style={{ whiteSpace: "pre-line" }} className="short-note text-[1rem] leading-[1.5] font-normal text-[#f4f4f4]">
        {paragraphs[0]}
      </p>

      {/* --- PARAGRAPH 2 (RESPONSIVE) --- 
          On Mobile: Inside the accordion (hidden by default).
          On Desktop: Visible immediately alongside paragraph 1. */}
      {paragraphs[1] && (
        <div
          className={`grid lg:block transition-[grid-template-rows,margin-top] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] mt-4" : "grid-rows-[0fr] lg:mt-4"
            }`}
        >
          <div className="overflow-hidden lg:overflow-visible">
            <p
              style={{ whiteSpace: "pre-line" }}
              className={`text-[1rem] leading-[1.5] font-normal transition-colors duration-300 ${isOpen
                ? "text-gray-200 border-l border-[#4deaa7]/40 pl-3 lg:text-[#f4f4f4] lg:border-none lg:pl-0"
                : "text-gray-200 border-l border-[#4deaa7]/40 pl-3 lg:text-[#f4f4f4] lg:border-none lg:pl-0"
                }`}
            >
              {paragraphs[1]}
            </p>
          </div>
        </div>
      )}

      {/* --- REST OF THE PARAGRAPHS (3+) --- 
          Always hidden behind the accordion trigger on both mobile and desktop */}
      {paragraphs.length > 2 && (
        <div
          className={`grid transition-[grid-template-rows,margin-top] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] mt-4" : "grid-rows-[0fr]"
            }`}
        >
          <div className="overflow-hidden">
            <p
              style={{ whiteSpace: "pre-line" }}
              className="text-[0.95rem] leading-[1.5] font-normal text-gray-200 border-l border-[#4deaa7]/40 pl-3"
            >
              {paragraphs.slice(2).join("\n\n")}
            </p>
          </div>
        </div>
      )}

      {/* Accordion Trigger Button — stays visible on all screens */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-[#4deaa7] hover:text-white text-[0.875rem] font-medium mt-4 transition-colors duration-200 flex items-center gap-1.5 focus:outline-none"
      >
        <span>{isOpen ? "Show less" : "Read full breakdown"}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  );
};

// --- Your Main Projects Section Component ---
const Projects = () => {
  return (
    <section id="work" className="navB">
      <div className="projects-container mb-[6rem] lg:mb-[8rem]">
        <Fade direction="up" triggerOnce={true} fraction={1}>
          <h2
            aria-label="work-section"
            className="my-projects mb-[4rem] text-[2.75rem] tracking-[-0.25rem] leading-[3.375rem] md:text-[3.125rem] md:leading-[3.75rem] lg:mb-[5rem] lg:leading-[4.375rem] lg:text-[4.375rem] xl:mb-[7rem] xl:leading-[5rem]"
          >
            Selected Work
          </h2>
        </Fade>

        {projectData.map((project, index) => (
          <div key={index} className="single-project1-grid">
            <Slide triggerOnce={true} fraction={1}>
              <div className="divider-line border-b-[0.0625rem] border-[#e5e5e5] mb-[2rem] lg:mb-[4rem]"></div>
            </Slide>
            <div className="single-project-wrapper grid grid-cols-1 gap-x-[1.5rem] lg:grid-cols-[2fr_minmax(0,_1fr)] landscape-mobile-orientation lg:gap-y-[2.5rem] mb-[6rem] lg:mb-[8rem] xl:mb-[10rem]">
              <div className="project-content">
                <div className="project-media">
                  <div className="media-wrapper border-[2rem] md:border-[3.2rem] lg:border-[3.5rem] border-[rgba(77,234,167,0.9)] relative">
                    <Slider chg={project.sliderChg} imgAlt={project.imgAlt} />
                  </div>
                </div>
              </div>

              <div className="project-description flex flex-col">
                <Fade direction="up" triggerOnce={true} fraction={1}>
                  <a
                    href={project.link}
                    target="_blank" rel="noopener noreferrer"
                    className="foodsearch-url w-fit"
                  >
                    <div className="project-details mt-[1rem] mb-[1rem] lg:mt-0 inline-flex items-baseline transition hover:translate-x-3 hover:scale-110 duration-300 group">
                      <div className="project-name text-[#f4f4f4] mr-[1rem] text-[2rem] leading-[2.625rem] lg:text-[2.75rem] group-hover:text-[#4deaa7]">
                        {project.name}
                      </div>
                      <div className="icons-link flex items-center">
                        <div>
                          <React.Fragment>
                            <BsArrowUpRight
                              size="25px"
                              className="arrow-icon border rounded-[100%] p-[5px] group-hover:bg-[#4deaa7] group-hover:text-gray-700 group-hover:border-[#4deaa7] lg:h-[30px] lg:w-[30px]"
                            />
                          </React.Fragment>
                        </div>
                      </div>
                    </div>
                  </a>
                </Fade>

                {/* Clean React-Reveal Parent Wrap */}
                <Fade direction="up" triggerOnce={true} fraction={1}>
                  <div>
                    {/* Integrated Accordion component here instead of raw <p> tag */}
                    <ProjectDescription fullDescription={project.description} />
                  </div>
                </Fade>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Projects;